/**
 * Raw Config Modal
 * Shows connector and processElement as editable JSON tabs.
 * Each tab has an inline Edit | Tree toggle via JsonEditor's showTreeView prop.
 *
 * Part of the RawConfig feature — lives here for easy removal.
 */

import React, { useState, useEffect } from 'react';
import { useWorkflowStore } from '@flow-studio/store';
import { connectorApiClient } from '@flow-studio/api';
import { JsonEditor } from '../Shared/JsonEditor';
import { ModalPortal } from '../Modals/ModalPortal';

interface RawConfigModalProps {
  nodeId: string;
  onClose: () => void;
}

type Tab = 'connector' | 'processElement';

export const RawConfigModal: React.FC<RawConfigModalProps> = ({ nodeId, onClose }) => {
  const { nodes, updateProcessElement } = useWorkflowStore();
  const node = nodes.find(n => n.id === nodeId);
  const pe = node?.data?.processElement;

  const [activeTab, setActiveTab] = useState<Tab>('connector');
  const [peJson, setPeJson]     = useState('');
  const [connJson, setConnJson] = useState('');
  const [peValid, setPeValid]   = useState(true);
  const [connValid, setConnValid] = useState(true);
  const [saved, setSaved]       = useState(false);
  const [autoWrapped, setAutoWrapped] = useState(false);
  const [connLoading, setConnLoading] = useState(false);

  // Populate processElement tab from store (always current)
  useEffect(() => {
    if (pe) setPeJson(JSON.stringify(pe, null, 2));
  }, [nodeId]);

  // Fetch connector fresh from the API to show the actual persisted configuration,
  // NOT the stale copy cached in processElement.
  useEffect(() => {
    // connectorID lives in three places depending on lifecycle:
    // 1. pe.connectorID — populated on workflow load from API
    // 2. pe.connector.ConnectorID — populated after onSave from ConnectorConfigDialog (PascalCase from API type)
    // 3. pe.connector.connectorID — camelCase fallback
    const connectorId = pe?.connectorID
      ?? (pe?.connector as any)?.ConnectorID
      ?? (pe?.connector as any)?.connectorID;

    if (!connectorId) {
      // No connector ID found - connector may not be configured yet
      setConnJson('{}');
      return;
    }

    setConnLoading(true);
    connectorApiClient
      .getById({ id: connectorId })
      .then(response => {
        if (response.success && response.data) {
          const rawConfig = response.data.Configuration;
          console.log('[RawConfigModal] Connector API response:', { connectorId, rawConfig, fullData: response.data });
          try {
            const parsed = rawConfig ? JSON.parse(rawConfig) : {};
            console.log('[RawConfigModal] Parsed configuration:', parsed);
            // Check if credentials are configured
            if (!parsed.credentialID && parsed.acceptedCredentialTypes?.length > 0) {
              console.warn('[RawConfigModal] Credentials not configured! This connector accepts:', parsed.acceptedCredentialTypes);
            }
            setConnJson(JSON.stringify(parsed, null, 2));
          } catch (e) {
            console.error('Failed to parse connector configuration:', e);
            setConnJson('{}');
          }
        } else {
          // API returned error or no data
          console.warn('Failed to fetch connector from API', response);
          setConnJson('{}');
        }
      })
      .catch((err) => {
        console.error('Error fetching connector:', err);
        setConnJson('{}');
      })
      .finally(() => setConnLoading(false));
  }, [nodeId]);

  const isJsonValid = (text: string): boolean => {
    try { JSON.parse(text); return true; } catch { return false; }
  };

  const handleApply = () => {
    if (!peValid || !connValid) return;
    try {
      const parsedPe     = JSON.parse(peJson);
      const parsedConfig = JSON.parse(connJson);
      // Re-wrap the edited config content back into the connector structure
      const existingConnector = pe?.connector ?? {};
      const updatedConnector  = { ...existingConnector, configuration: parsedConfig };

      // Some node guides (e.g. FORMNODE_RAWCONFIG_WORKFLOW_GUIDE.md) document pasting a
      // flat config object straight into this tab, with no "configuration" wrapper. Only
      // pe.configuration is ever serialized on save (see workflowMappers.ts), so a flat
      // paste lands as unread sibling keys on pe and silently vanishes on save. Detect an
      // already-configured node whose paste dropped the wrapper and auto-wrap it instead
      // of merging it flat, so the edit actually reaches persistence.
      const isFlatConfigPaste = pe?.configuration != null && parsedPe.configuration === undefined;
      const peUpdate = isFlatConfigPaste ? { configuration: parsedPe } : parsedPe;

      updateProcessElement(nodeId, { ...peUpdate, connector: updatedConnector });
      setAutoWrapped(isFlatConfigPaste);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch { /* validation guards this */ }
  };

  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '8px 20px',
    fontSize: '12px',
    fontWeight: active ? 600 : 400,
    color: active ? '#fff' : 'rgba(255,255,255,0.5)',
    background: active ? 'rgba(48,155,72,0.25)' : 'transparent',
    border: 'none',
    borderBottom: active ? '2px solid #309B48' : '2px solid transparent',
    cursor: 'pointer',
    transition: 'all 0.15s',
  });

  return (
    <ModalPortal>
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          background: 'rgba(0,0,0,0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'auto',
        }}
        onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div
          className="nodrag nopan"
          style={{
            background: '#0f172a',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: '12px',
            width: '90vw',
            maxWidth: '1400px',
            height: '85vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 24px 60px rgba(0,0,0,0.7)',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
            <span style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>
              Raw Config — <span style={{ color: '#94a3b8' }}>{pe?.displayName ?? nodeId}</span>
            </span>
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: '20px', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}
              title="Close"
            >×</button>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.08)', padding: '0 16px', flexShrink: 0 }}>
            <button style={tabStyle(activeTab === 'connector')}      onClick={() => setActiveTab('connector')}>Connector</button>
            <button style={tabStyle(activeTab === 'processElement')} onClick={() => setActiveTab('processElement')}>Process Element</button>
          </div>

          {/* Explainer */}
          <div style={{ padding: '8px 24px 0', flexShrink: 0 }}>
            {activeTab === 'connector' && (
              <div>
                <p style={{ margin: 0, fontSize: '11px', color: 'rgba(255,255,255,0.38)', lineHeight: 1.5 }}>
                  <strong style={{ color: 'rgba(255,255,255,0.55)', fontWeight: 600 }}>Connector configuration</strong>
                  {' '}— application settings and credentials specific to this node.
                </p>
                {connJson && !connJson.includes('credentialID') && connJson.includes('acceptedCredentialTypes') && (
                  <p style={{ margin: '6px 0 0 0', fontSize: '11px', color: '#f59e0b', fontWeight: 500 }}>
                    ⚠ Credentials not configured. Configure them via Node Properties → Configure Connector.
                  </p>
                )}
              </div>
            )}
            {activeTab === 'processElement' && (
              <p style={{ margin: 0, fontSize: '11px', color: 'rgba(255,255,255,0.38)', lineHeight: 1.5 }}>
                <strong style={{ color: 'rgba(255,255,255,0.55)', fontWeight: 600 }}>Process Element</strong>
                {' '}holds process orchestration, UI layout, and all design-time configuration for this node.
              </p>
            )}
          </div>

          {/* Body */}
          <div style={{ flex: 1, overflow: 'auto', padding: '12px 24px 20px', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {activeTab === 'connector' && (
              connLoading
                ? <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px', padding: '12px 0' }}>Loading connector…</div>
                : <JsonEditor
                    value={connJson}
                    onChange={(v) => { setConnJson(v); setConnValid(isJsonValid(v)); }}
                    placeholder="Connector JSON..."
                    showFormatButton
                    showCopyButton
                    showTreeView
                    showValidationStatus
                    fill
                  />
            )}
            {activeTab === 'processElement' && (
              <JsonEditor
                value={peJson}
                onChange={(v) => { setPeJson(v); setPeValid(isJsonValid(v)); }}
                placeholder="Process element JSON..."
                showFormatButton
                showCopyButton
                showTreeView
                showValidationStatus
                fill
              />
            )}
          </div>

          {/* Footer */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px', padding: '14px 24px', borderTop: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
            {saved && (
              <span style={{ fontSize: '12px', color: '#22c55e' }}>
                {autoWrapped ? '✓ Applied (wrapped under configuration)' : '✓ Applied'}
              </span>
            )}
            <button
              onClick={onClose}
              style={{ padding: '7px 18px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'rgba(255,255,255,0.7)', fontSize: '12px', cursor: 'pointer' }}
            >
              Close
            </button>
            <button
              onClick={handleApply}
              disabled={!peValid || !connValid}
              style={{
                padding: '7px 22px', borderRadius: '6px', border: 'none',
                background: peValid && connValid ? '#309B48' : '#374151',
                color: '#fff', fontSize: '12px', fontWeight: 600,
                cursor: peValid && connValid ? 'pointer' : 'not-allowed',
              }}
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};
